import { useEffect, useMemo, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type CompTicket } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from '../components/Icon.js';
import { Topbar } from './AdminLayout.js';
import { fmtDateTime } from '../utils/format.js';

export function CompTicketsList(): JSX.Element {
  const nav = useNavigate();
  const [items, setItems] = useState<CompTicket[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [activeProfileId] = useActiveProfileId();

  useEffect(() => {
    setItems(null);
    setErr(null);
    api
      .listCompTickets()
      .then(setItems)
      .catch((e: Error) => setErr(e.message));
  }, [activeProfileId]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((t) =>
      [t.ticketNumber, t.customerName, t.customerEmail, t.customerPhone, t.product?.name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [items, q]);

  return (
    <>
      <Topbar
        title="Промобилеты"
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => nav('/admin/comp-tickets/new')}
          >
            <Icon name="plus" size={13} />
            Выписать билет
          </button>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        {err && (
          <div
            className="card"
            style={{
              marginBottom: 14,
              color: 'var(--danger-fg)',
              background: 'var(--danger-subtle-bg)',
            }}
          >
            <div className="card-body">{err}</div>
          </div>
        )}
        <div className="card">
          <div
            style={{
              padding: '14px 20px',
              borderBottom: '1px solid var(--hairline)',
            }}
          >
            <input
              type="text"
              className="input"
              placeholder="Поиск по номеру, имени, email, телефону или продукту…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ maxWidth: 420 }}
            />
          </div>
          {items === null ? (
            <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>Загрузка…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>
              {q ? 'Ничего не найдено' : 'Промобилетов пока нет'}
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Билет</th>
                  <th>Гость</th>
                  <th>Продукт</th>
                  <th>Выписан</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <div
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontWeight: 600,
                        }}
                      >
                        {t.ticketNumber ?? '—'}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}><bdi>{t.customerName}</bdi></div>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>
                        {t.customerEmail}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}><bdi>{t.product?.name ?? '—'}</bdi></td>
                    <td style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                      {fmtDateTime(t.ticketIssuedAt ?? t.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
